import type { FunctionComponent } from "react";

import type { SlotConfigProps } from "scmp-app/components/advertisement/ad-slots/ad-slot";
import { type Props as OutstreamAdSlotProps } from "scmp-app/components/advertisement/ad-slots/outstream-ad-slot";
import {
  isNativeAdUnit,
  isOutstreamAdUnit,
  type NativeAdUnit,
} from "scmp-app/components/advertisement/ad-slots/types";
import { useRenderContext } from "scmp-app/components/schema-render/content";

import { Container, InlineNativeSlot, InlineOutstreamAdSlot, InlineSlot } from "./styles";

export type Props = {
  className?: string;
  entityId: string;
  slot: InlineSlotConfig | OutstreamAdSlotProps;
};

type InlineSlotConfig = WithoutLabelProps<SlotConfigProps>;

type NativeInlineSlotConfig = InlineSlotConfig & { adUnit: NativeAdUnit };

// Label policy is owned by the slot components below, so configs cannot set it.
type WithoutLabelProps<T> = T extends unknown
  ? Omit<T, "withLabel" | "withLabelBackground">
  : never;

const isNativeInlineSlotConfig = (slot: InlineSlotConfig): slot is NativeInlineSlotConfig =>
  isNativeAdUnit(slot.adUnit);

export const InlineAdSlot: FunctionComponent<Props> = ({ className, entityId, slot }) => {
  const { advertising } = useRenderContext();

  if (isOutstreamAdUnitProps(slot)) {
    return (
      <InlineOutstreamAdSlot
        className={className}
        {...advertising}
        {...slot}
        targeting={{
          ...advertising?.targeting,
          ...slot?.targeting,
        }}
      />
    );
  }

  if (isNativeInlineSlotConfig(slot)) {
    return (
      <InlineNativeSlot
        className={className}
        sizes={["fluid"]}
        {...advertising}
        {...slot}
        targeting={{
          ...advertising?.targeting,
          ...slot?.targeting,
        }}
      />
    );
  }

  return (
    <Container className={className}>
      <InlineSlot
        autoRefreshOptions={{
          scrollBackRefreshOptions: {
            slotArticleEntityId: entityId,
          },
        }}
        sizes={[
          [300, 250],
          [300, 600],
          [320, 480],
        ]}
        withBackground
        withLabel
        withSticky
        {...advertising}
        {...slot}
        targeting={{
          ...advertising?.targeting,
          ...slot?.targeting,
        }}
      />
    </Container>
  );

  function isOutstreamAdUnitProps(slot: Props["slot"]): slot is OutstreamAdSlotProps {
    return isOutstreamAdUnit(slot.adUnit);
  }
};

InlineAdSlot.displayName = "InlineAdSlot";
